// Single source of truth for all API path builders.
// Mirrors the URL structure defined in doc/API_CONVENTIONS.md.

export const endpoints = {
  // Leagues
  leagues: {
    list:     () => '/v1/leagues',
    create:   () => '/v1/leagues',
    detail:   (id: string) => `/v1/leagues/${id}`,
    delete:   (id: string) => `/v1/leagues/${id}`,
    members:  (id: string) => `/v1/leagues/${id}/members`,
    favorite: (id: string) => `/v1/leagues/${id}/favorite`,
    teamName: (id: string) => `/v1/leagues/${id}/my-team-name`,
  },

  // League memberships (join / leave)
  leagueMemberships: {
    join:  () => '/v1/league-memberships',
    leave: (membershipId: string) => `/v1/league-memberships/${membershipId}`,
  },

  // Squads
  squads: {
    my:          (leagueId: string) => `/v1/leagues/${leagueId}/my-squad`,
    userTeam:    (leagueId: string, userId: string, gameweekId?: string) =>
      gameweekId
        ? `/v1/leagues/${leagueId}/teams/${userId}?gameweekId=${gameweekId}`
        : `/v1/leagues/${leagueId}/teams/${userId}`,
    save:        (squadId: string)  => `/v1/squads/${squadId}/players`,
    transfer:    (leagueId: string) => `/v1/leagues/${leagueId}/squad/transfers`,
    copyPreview: (squadId: string, sourceSquadId: string) =>
      `/v1/squads/${squadId}/copy-preview?sourceSquadId=${encodeURIComponent(sourceSquadId)}`,
  },

  // Standings
  standings: {
    list: (leagueId: string) => `/v1/leagues/${leagueId}/standings`,
  },

  // League detail sub-resources
  leagueDetail: {
    gameweekStatus:  (leagueId: string) => `/v1/leagues/${leagueId}/gameweek-status` as const,
    mostPickedPlayer: (leagueId: string, gameweekId?: string) =>
      gameweekId
        ? `/v1/leagues/${leagueId}/most-picked-player?gameweekId=${gameweekId}`
        : `/v1/leagues/${leagueId}/most-picked-player` as const,
    tableByGw: (leagueId: string, gameweekId?: string) =>
      gameweekId
        ? `/v1/leagues/${leagueId}/table?gameweekId=${gameweekId}`
        : `/v1/leagues/${leagueId}/table` as const,
    fixtures: (leagueId: string) => `/v1/leagues/${leagueId}/fixtures` as const,
  },

  // Players
  players: {
    list:   () => '/v1/players',
    detail: (id: string) => `/v1/players/${id}`,
  },

  // Gameweeks
  gameweeks: {
    list:    (competitionId: string) => `/v1/gameweeks?competitionId=${competitionId}`,
    current: (competitionId: string) => `/v1/competitions/${competitionId}/gameweeks/current`,
  },

  // Matches
  matches: {
    list:   (gameweekId: string) => `/v1/gameweeks/${gameweekId}/matches`,
    detail: (id: string) => `/v1/matches/${id}`,
  },

  // User / profile
  users: {
    me:           () => '/v1/users/me',
    update:       () => '/v1/users/me',
    personalData: () => '/v1/users/me/personal-data',
    pixKey:       () => '/v1/users/me/pix-key',
  },

  // Competitions
  competitions: {
    list:     () => '/v1/competitions',
    fixtures: (competitionId: string) => `/v1/competitions/${competitionId}/fixtures`,
    teams:    (competitionId: string) => `/v1/competitions/${competitionId}/teams`,
  },

  // Subscriptions
  subscriptions: {
    current: () => '/v1/subscriptions/current',
    upgrade: () => '/v1/subscriptions/upgrade',
  },

  // Admin
  admin: {
    me:                  () => '/v1/admin/me',
    lockGameweek:        (gameweekId: string) => `/v1/gameweeks/${gameweekId}/lock`,
    fetchGameweekStats:  (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/fetch-stats`,
    processGameweek:     (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/process`,
    finalizeGameweek:    (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/finalize`,
    resetGameweek:       (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/reset`,
    reopenTransferWindow: (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/reopen-transfer-window`,
    schedules: {
      list:   (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/schedules`,
      create: (gameweekId: string) => `/v1/admin/gameweeks/${gameweekId}/schedules`,
      cancel: (scheduleId: string) => `/v1/admin/schedules/${scheduleId}`,
    },
    persons: {
      list: (
        competitionId: string,
        opts: { teamId?: string; position?: string } = {},
      ) => {
        const params = new URLSearchParams()
        if (opts.teamId)   params.set('teamId',   opts.teamId)
        if (opts.position) params.set('position', opts.position)
        const qs = params.toString()
        return qs
          ? `/v1/admin/competitions/${competitionId}/persons?${qs}`
          : `/v1/admin/competitions/${competitionId}/persons`
      },
      updateDisplayName: (personId: string) => `/v1/admin/persons/${personId}`,
    },
    players: {
      updatePosition: (playerId: string) => `/v1/admin/players/${playerId}`,
      updatePrice:    (playerId: string) => `/v1/admin/players/${playerId}/price`,
    },
    seedRosters: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/seed-rosters-from-api`,
    syncTeams: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/sync-teams`,
    syncSchedule: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/sync-schedule`,
    extendH2HFixtures: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/extend-h2h-fixtures`,
    syncInjuries: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/sync-injuries`,
    syncEliminations: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/sync-eliminations`,
    previewPrices: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/preview-prices`,
    applyPrices: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/apply-prices`,

    // Sobrevivente admin — competition catalogue + results-only ingestion.
    // Deliberately excludes every fantasy surface (stats, process, prices, rosters).
    createCompetition: () => '/v1/admin/competitions',
    updateCompetition: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}`,
    syncGameweekResults: (gameweekId: string) =>
      `/v1/admin/gameweeks/${gameweekId}/sync-results`,
    syncCompetitionResults: (competitionId: string) =>
      `/v1/admin/competitions/${competitionId}/sync-results`,
    setMatchResult: (matchId: string) =>
      `/v1/admin/matches/${matchId}/result`,
  },

  // Sobrevivente (survivor-pool side game)
  survivorGames: {
    list:           () => '/v1/survivor-games',
    create:         () => '/v1/survivor-games',
    detail:         (gameId: string) => `/v1/survivor-games/${gameId}`,
    update:         (gameId: string) => `/v1/survivor-games/${gameId}`,
    updateTeamName: (gameId: string) => `/v1/survivor-games/${gameId}/my-team-name`,
    delete:         (gameId: string) => `/v1/survivor-games/${gameId}`,
    regenerateCode: (gameId: string) => `/v1/survivor-games/${gameId}/invite-code`,
    restart:        (gameId: string) => `/v1/survivor-games/${gameId}/restart`,
    hallOfFame:     (gameId: string) => `/v1/survivor-games/${gameId}/hall-of-fame`,
    standings:      (gameId: string) => `/v1/survivor-games/${gameId}/standings`,
    myPicks:        (gameId: string) => `/v1/survivor-games/${gameId}/my-picks`,
    submitPick:     (gameId: string) => `/v1/survivor-games/${gameId}/picks`,
    picksByGw:      (gameId: string, gameweekId: string) =>
      `/v1/survivor-games/${gameId}/picks?gameweekId=${gameweekId}`,
    availableTeams: (gameId: string, gameweekId: string) =>
      `/v1/survivor-games/${gameId}/available-teams?gameweekId=${gameweekId}`,
  },
  survivorParticipations: {
    join:  () => '/v1/survivor-participations',
    leave: (participationId: string) => `/v1/survivor-participations/${participationId}`,
  },
} as const
